import * as PIXI from "pixi";

import { DeobfuPiouPiouzLevel } from "piou-core/types";
import Level from "../level/Level";
import Piou from "./Piou";
import MTSprite from "./MTSprite";
import Mouse from "../utils/Mouse";
import Part from "./Part";
import ActionManager from "./ActionManager";
import Keyboard from "../utils/Keyboard";
import { animatePixiFilter } from "../utils/PixiAnimation";

const SPEEDS = [1, 6, 12];

export enum GameOverType {
  MORE_THAN_GOAL,
  GOAL,
  LESS_THAN_GOAL,
  NO_PIOU,
}

export default class Game extends PIXI.Container {
  app: PIXI.Application;

  lvlInfo: DeobfuPiouPiouzLevel;
  level: Level;

  mouse: Mouse;
  actionManager: ActionManager;

  spriteContainer: PIXI.Container;
  uiContainer: PIXI.Container;

  sprites: MTSprite[];

  speed: number = 1;
  paused: boolean = false;

  // UI
  private _piouGoal: number;
  private _piouCount: number;
  get piouGoal() {
    return this._piouGoal;
  }
  set piouGoal(value: number) {
    this._piouGoal = value;
    this.emit("uiUpdate", "piou");
    if (this.piouCount === 0) {
      this.endGame();
    }
  }
  get piouCount() {
    return this._piouCount;
  }
  set piouCount(value: number) {
    this._piouCount = value;
    this.emit("uiUpdate", "piou");
  }

  get piouList() {
    return this.sprites.filter((s) => s instanceof Piou) as Piou[];
  }
  get partList() {
    return this.sprites.filter((s) => s instanceof Part) as Part[];
  }

  constructor(
    app: PIXI.Application,
    lvlInfo: DeobfuPiouPiouzLevel,
    level: Level
  ) {
    super();
    this.app = app;
    this.name = "Game";
    this.lvlInfo = lvlInfo;
    this.sprites = [];

    this._piouGoal = lvlInfo._piouz;
    this._piouCount = 0;

    this.level = level;
    this.spriteContainer = new PIXI.Container();
    this.uiContainer = new PIXI.Container();
    this.addChild(level);
    this.addChild(this.spriteContainer);
    this.addChild(this.uiContainer);

    this.hitArea = new PIXI.Rectangle(0, 0, level.lvlWidth, level.lvlHeight);
    this.mouse = new Mouse(this);
    this.actionManager = new ActionManager(this);

    for (const piou of this.lvlInfo.data.piou) {
      const p = new Piou(this, piou[0], piou[1]);
      p.bouncer?.setPos(piou[0], piou[1]);
      p.updatePos();
      p.initWalk();
      p.checkExit();
    }

    app.ticker.maxFPS = 40;
    app.ticker.add(() => {
      if (this.paused) return;
      for (let i = 0; i < this.speed; i++) {
        this.update();
      }
    });

    // Keybard shortcuts
    const pauseFilter = new PIXI.filters.ColorMatrixFilter();
    pauseFilter.blackAndWhite(false);
    this.paused = true;
    PIXI.Ticker.shared.autoStart = false;
    PIXI.Ticker.shared.stop();
    this.filters = [pauseFilter];

    Keyboard.down(["KeyP", "Space"], () => {
      this.paused = !this.paused;
      this.filters = [pauseFilter];

      if (this.paused) {
        animatePixiFilter(this, pauseFilter, true);
        PIXI.Ticker.shared.stop();
      } else {
        animatePixiFilter(this, pauseFilter, false);
        PIXI.Ticker.shared.start();
      }
    });
    Keyboard.down("KeyT", () => {
      this.speed = SPEEDS[(SPEEDS.indexOf(this.speed) + 1) % SPEEDS.length];
    });
    Keyboard.down(["ShiftLeft", "ShiftRight"], () => {
      this.speed =
        SPEEDS[
          (SPEEDS.indexOf(this.speed) + 1 + SPEEDS.length) % SPEEDS.length
        ];
    });
    Keyboard.up(["ShiftLeft", "ShiftRight"], () => {
      this.speed =
        SPEEDS[
          (SPEEDS.indexOf(this.speed) - 1 + SPEEDS.length) % SPEEDS.length
        ];
    });
  }

  update() {
    // Move sprites
    for (const sprite of this.sprites) {
      sprite.update();
    }

    // Handle actions
    for (const piou of this.piouList) {
      piou.currentAction?.update();
    }
  }

  addSprite(sprite: MTSprite) {
    this.sprites.push(sprite);
    this.spriteContainer.addChild(sprite.root);
  }

  killSprite(sprite: MTSprite) {
    let idx = this.sprites.indexOf(sprite);
    if (idx !== -1) {
      this.sprites.splice(idx, 1);
      this.spriteContainer.removeChild(sprite.root);
    }
  }

  endGame() {
    if (this.piouGoal === this.lvlInfo._piouz) {
      this.emit("endGame", GameOverType.NO_PIOU);
    } else if (this.piouGoal > 0) {
      this.emit("endGame", GameOverType.LESS_THAN_GOAL);
    } else if (this.piouGoal < 0) {
      this.emit("endGame", GameOverType.MORE_THAN_GOAL);
    } else {
      this.emit("endGame", GameOverType.GOAL);
    }
  }
}
